import { c as _c } from "react/compiler-runtime";
import React, { ComponentProps, useCallback, useMemo } from 'react';
import { useArrayPagination } from '../../hooks/useArrayPagination';
import { FilterPipelineFilter, useFilterPipeline } from '../../hooks/useFilterPipeline/useFilterPipeline';
import { CreateEntityButton } from '../../misc/CreateEntityButton/CreateEntityButton';
import { FilterBar } from '../../misc/FilterBar/FilterBar';
import { Pagination } from '../../misc/Pagination/Pagination';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { PageContentContainer } from '../PageContentContainer/PageContentContainer';
import { IconConfig, PageContentHeader, PageContentHeaderProps } from '../PageContentHeader/PageContentHeader';
import styles from './ManageEntitiesContentLayout.module.css';
export type ManageEntitiesContentLayoutProps<TEntity, TCreateEntityButtonItem> = PropsWithoutChildren<ComponentProps<'div'>> & {
  readonly headerConfig: HeaderConfig;
  readonly entities: Array<TEntity>;
  readonly renderEntities: (currentPageEntities: Array<TEntity>) => React.JSX.Element;
  readonly createEntityButtonConfig: CreateEntityButtonConfig<TCreateEntityButtonItem>;
  /**
   * default: true
   */
  readonly usePagination?: boolean;
  /**
   * default: []
   */
  readonly filters?: Array<FilterPipelineFilter<TEntity, any>>;
};
export type HeaderConfig = {
  iconConfig: IconConfig;
  title: string;
  /* gets placed left of the create entity button */
  renderAdditionalRightContent?: () => Array<React.JSX.Element>;
};
export type CreateEntityButtonConfig<TCreateEntityButtonItem> = {
  visible: false;
} | {
  visible: true;
  text: string;
  dropdownItems: Array<TCreateEntityButtonItem>;
  getDropdownItemDisplayText: (item: TCreateEntityButtonItem) => string;
  getDropdownItemKey: (item: TCreateEntityButtonItem) => string;
  onCreateEntity: (item: TCreateEntityButtonItem | null) => void;
};
export const ManageEntitiesContentLayout = React.memo(props => {
  const $ = _c(42);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props,
      ignoreChildren: true
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let createEntityButtonConfig;
  let entities;
  let headerConfig;
  let remainingProps;
  let renderEntities;
  let t1;
  let t2;
  if ($[2] !== t0) {
    ({
      entities,
      createEntityButtonConfig,
      renderEntities,
      headerConfig,
      usePagination: t1,
      filters: t2,
      ...remainingProps
    } = t0);
    $[2] = t0;
    $[3] = createEntityButtonConfig;
    $[4] = entities;
    $[5] = headerConfig;
    $[6] = remainingProps;
    $[7] = renderEntities;
    $[8] = t1;
    $[9] = t2;
  } else {
    createEntityButtonConfig = $[3];
    entities = $[4];
    headerConfig = $[5];
    remainingProps = $[6];
    renderEntities = $[7];
    t1 = $[8];
    t2 = $[9];
  }
  const usePagination = t1 === undefined ? true : t1;
  let t3;
  if ($[10] !== t2) {
    t3 = t2 === undefined ? [] : t2;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const filters = t3;
  let t4;
  if ($[12] !== entities || $[13] !== filters) {
    t4 = {
      items: entities,
      filters
    };
    $[12] = entities;
    $[13] = filters;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const {
    filteredItems: filteredEntities,
    handle: filterPipelineHandle
  } = useFilterPipeline(t4);
  const {
    paginationHandle,
    currentPageItems
  } = useArrayPagination(filteredEntities, 10);
  const entitiesToRender = usePagination ? currentPageItems : filteredEntities;
  let t5;
  if ($[15] !== entitiesToRender || $[16] !== renderEntities) {
    t5 = renderEntities(entitiesToRender);
    $[15] = entitiesToRender;
    $[16] = renderEntities;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const renderedEntities = t5;
  let t6;
  if ($[18] !== createEntityButtonConfig.dropdownItems || $[19] !== createEntityButtonConfig.getDropdownItemDisplayText || $[20] !== createEntityButtonConfig.getDropdownItemKey || $[21] !== createEntityButtonConfig.onCreateEntity || $[22] !== createEntityButtonConfig.text || $[23] !== createEntityButtonConfig.visible || $[24] !== headerConfig) {
    t6 = () => {
      const additionalElements = headerConfig.renderAdditionalRightContent?.();
      const elements = additionalElements ? [...additionalElements] : [];
      if (createEntityButtonConfig.visible) {
        elements.push(<CreateEntityButton text={createEntityButtonConfig.text} key="ManageEntitiesContentLayout.CreateEntityButton" dropdownItems={createEntityButtonConfig.dropdownItems} getDropdownItemDisplayText={createEntityButtonConfig.getDropdownItemDisplayText} getDropdownItemKey={createEntityButtonConfig.getDropdownItemKey} onCreateEntity={createEntityButtonConfig.onCreateEntity} data-testid="ManageEntitiesContentLayout.CreateEntityButton" />);
      }
      return elements;
    };
    $[18] = createEntityButtonConfig.dropdownItems;
    $[19] = createEntityButtonConfig.getDropdownItemDisplayText;
    $[20] = createEntityButtonConfig.getDropdownItemKey;
    $[21] = createEntityButtonConfig.onCreateEntity;
    $[22] = createEntityButtonConfig.text;
    $[23] = createEntityButtonConfig.visible;
    $[24] = headerConfig;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  const renderRightContentElements = t6;
  let t7;
  if ($[26] !== filterPipelineHandle || $[27] !== filters.length) {
    t7 = () => {
      if (!filters.length) {
        return null;
      }
      return <FilterBar handle={filterPipelineHandle} data-testid="ManageEntitiesContentLayout.FilterBar" />;
    };
    $[26] = filterPipelineHandle;
    $[27] = filters.length;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  const renderFilterBar = t7;
  let t8;
  if ($[29] !== headerConfig.iconConfig || $[30] !== headerConfig.title || $[31] !== renderFilterBar || $[32] !== renderRightContentElements) {
    t8 = <PageContentHeader iconConfig={headerConfig.iconConfig} title={headerConfig.title} renderRightContentElements={renderRightContentElements} renderAdditionalBottomContent={renderFilterBar} />;
    $[29] = headerConfig.iconConfig;
    $[30] = headerConfig.title;
    $[31] = renderFilterBar;
    $[32] = renderRightContentElements;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  let t9;
  if ($[34] !== paginationHandle || $[35] !== usePagination) {
    t9 = usePagination ? <Pagination paginationHandle={paginationHandle} className={styles.pagination} data-testid="ManageEntitiesContentLayout.Pagination" /> : null;
    $[34] = paginationHandle;
    $[35] = usePagination;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  let t10;
  if ($[37] !== remainingProps || $[38] !== renderedEntities || $[39] !== t8 || $[40] !== t9) {
    t10 = <PageContentContainer {...remainingProps}>{t8}{renderedEntities}{t9}</PageContentContainer>;
    $[37] = remainingProps;
    $[38] = renderedEntities;
    $[39] = t8;
    $[40] = t9;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  return t10;
}) as <TEntity, TCreateEntityButtonItem>(props: ManageEntitiesContentLayoutProps<TEntity, TCreateEntityButtonItem>) => React.JSX.Element;